import React, { useEffect, useState } from 'react';
import { FiArrowUpRight, FiCopy, FiCheck, FiStar } from 'react-icons/fi';
import { exchanges, adminUrl } from './exchangeData';
import '../services/service-detail.css';
import './exchanges.css';

const external = { target: '_blank', rel: 'noopener noreferrer' };

function ExchangeRow({ exchange }) {
  const recommended = exchange.name === 'BloFin';
  const [copyState, setCopyState] = useState('');
  async function copyCode() {
    try {
      await navigator.clipboard.writeText(exchange.code);
      setCopyState('Copied');
    } catch {
      setCopyState('Select and copy the code above');
    }
  }
  return (
    <article className={`exchange-row${recommended ? ' exchange-row--recommended' : ''}`} style={{ '--exchange-color': exchange.color }}>
      {recommended && <span className="exchange-recommended-badge"><FiStar aria-hidden="true" />CRYPTOXNFT RECOMMENDED</span>}
      <div className="exchange-identity"><img className="exchange-logo" src={`/assets/images/exchanges/${exchange.name.toLowerCase()}.ico`} alt="" width="36" height="36" /><h3>{exchange.name}</h3></div>
      <div className="exchange-offer"><strong>{exchange.offer}</strong><p>{exchange.detail}</p></div>
      <div className="exchange-code-wrap">
        {exchange.code ? <button type="button" className="exchange-code" onClick={copyCode} aria-label={`Copy ${exchange.name} referral code ${exchange.code}`}><code>{exchange.code}</code>{copyState === 'Copied' ? <FiCheck /> : <FiCopy />}</button> : <span className="exchange-link-code">Included in partner link</span>}
        <span className="exchange-copy-status" role="status">{copyState}</span>
      </div>
      <div className="exchange-verification">
        {exchange.verificationSource ? (
          <a className={`exchange-kyc-badge exchange-kyc-badge--${exchange.kyc}`} href={exchange.verificationSource} {...external} title={exchange.verification} aria-label={`${exchange.name}: ${exchange.verification}`}>
            {exchange.kyc === 'required' ? 'KYC' : 'No KYC'}
          </a>
        ) : (
          <span className={`exchange-kyc-badge exchange-kyc-badge--${exchange.kyc}`} title={exchange.verification}>{exchange.kyc === 'required' ? 'KYC' : 'No KYC'}</span>
        )}
        <small>{exchange.verificationNote}</small>
      </div>
      <div className="exchange-join-wrap">
        <a className="exchange-join" href={exchange.url} {...external} aria-label={`Join ${exchange.name}`}>Join {exchange.name}<FiArrowUpRight /></a>
        <small className="exchange-join-hint">Register → Send UID → <strong>FREE VIP</strong></small>
      </div>
    </article>
  );
}

function FeeComparison() {
  const [orderType, setOrderType] = useState('taker');
  const [notional, setNotional] = useState('10000');
  const validAmount = notional.trim() !== '' && Number.isFinite(Number(notional)) && Number(notional) >= 0 && Number(notional) <= 100000000;
  const amount = validAmount ? Number(notional) : 0;
  const formatAmount = value => value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <section className="exchange-fees exchange-section" id="fees" aria-labelledby="fees-title">
      <div className="exchange-fee-intro">
        <span className="exchange-section-label">THE COST OF A TRADE</span>
        <h2 id="fees-title">Small fees. <br />Real differences.</h2>
        <p>Compare the published perpetual futures fee for the same order on each exchange.</p>
        <div className="exchange-calculator">
          <fieldset><legend>Order type</legend><div className="exchange-segments">{['maker', 'taker'].map(type => <button type="button" key={type} aria-pressed={orderType === type} onClick={() => setOrderType(type)}>{type === 'maker' ? 'Maker' : 'Taker'}</button>)}</div></fieldset>
          <label htmlFor="exchange-notional">Order value <span>USDT</span></label>
          <input id="exchange-notional" type="number" min="0" max="100000000" step="any" value={notional} onChange={event => setNotional(event.target.value)} aria-invalid={!validAmount} aria-describedby="exchange-fee-note" />
          <p id="exchange-fee-note" className="exchange-small">{validAmount ? 'Notional order value, not your margin. One execution only; opening and closing are charged separately.' : 'Enter an order value between 0 and 100,000,000 USDT.'}</p>
        </div>
      </div>
      <div className="exchange-chart">
        <div className="exchange-chart-heading"><h3>Estimated fee per execution</h3><span>USDT</span></div>
        <div aria-live="polite" aria-atomic="true">
          {exchanges.map(exchange => <div className="exchange-bar-row" key={exchange.name}>
            <a href={exchange.source} {...external} aria-label={`${exchange.name} official fee source`}>{exchange.name}<FiArrowUpRight /></a>
            <div className="exchange-bar-track" aria-hidden="true"><div style={{ width: `${(exchange[orderType] ?? 0) / 0.08 * 100}%`, backgroundColor: exchange.color }} /></div>
            <div className="exchange-fee-value"><strong>{validAmount && exchange[orderType] !== null ? formatAmount(amount * exchange[orderType] / 100) : '—'}</strong><span>{exchange[orderType] === null ? 'Check rate' : `${exchange[orderType].toFixed(3)}%`}</span></div>
          </div>)}
        </div>
      </div>
    </section>
  );
}

export default function Exchanges() {
  useEffect(() => {
    const previous = document.title;
    document.title = 'Partner Exchanges & VIP Access | CRYPTOXNFT';
    return () => { document.title = previous; };
  }, []);
  return (
    <main className="exchanges-page service-detail-page">
      <div className="exchanges-container">
        <section className="exchange-hero">
          <div className="exchange-hero-copy">
            <h1>Choose Your Exchange.<br /><span>Unlock <span style={{ color: 'var(--exchange-gold)' }}>FREE VIP</span></span></h1>
            <p>Join through our links. Use our code and get <strong className="exchange-vip-subheader">FREE VIP</strong> access.</p>
            <div className="exchange-hero-actions"><a className="exchange-button" href="#partners">Find your exchange</a><a className="exchange-button exchange-button--secondary" href="#fees">Compare fees</a></div>
          </div>
        </section>

        <section id="partners" className="exchange-section exchange-directory" aria-labelledby="partners-title">
          <div className="exchange-section-heading"><div><span className="exchange-section-label">OUR PARTNER NETWORK</span><h2 id="partners-title">Find your exchange.</h2></div></div>
          <div className="exchange-table-labels" aria-hidden="true"><span>EXCHANGE</span><span>PARTNER BENEFIT</span><span>REFERRAL CODE</span><span>VERIFICATION</span><span>GET STARTED</span></div>
          <div className="exchange-list">{exchanges.map(exchange => <ExchangeRow key={exchange.name} exchange={exchange} />)}</div>
        </section>

        <FeeComparison />

        <section id="claim" className="exchange-section exchange-claim" aria-labelledby="claim-title">
          <div className="exchange-section-heading"><div><span className="exchange-section-label">MAKE IT YOURS</span><h2 id="claim-title">Unlock Your <span className="exchange-gold-text">FREE VIP</span></h2></div><p>Already registered through one of our referral links?<br />Send your exchange name + UID to get verified.</p></div>
          <ol className="exchange-steps">
            <li><span>01</span><div><h3>Create your account</h3><p>Choose an exchange above and register through our link/code.</p></div></li>
            <li><span>02</span><div><h3>Copy your UID</h3><p>Find your UID inside your exchange account.</p></div></li>
            <li><span>03</span><div><h3>Send UID to Admin</h3><p>Send your exchange name + UID to<br /><strong className="exchange-gold-text">@CRYPTOXNFTSIGNALS.</strong></p></div></li>
          </ol>
          <a className="exchange-button exchange-verify-button" href={adminUrl} {...external}>VERIFY UID &amp; GET FREE VIP<FiArrowUpRight aria-hidden="true" /></a>
        </section>
      </div>
    </main>
  );
}
